import React, { useEffect, useMemo, useState } from "react";
import { Sheet, SheetContent } from "@/components/ui/sheet";
import { Minus, Plus, Trash2, ShoppingCart, Loader2, ChevronLeft, Truck, Lock } from "lucide-react";
import { useMerchCart } from "@/context/MerchCartContext";
import { useAuth } from "@/context/AuthContext";
import { api, money } from "@/lib/api";
import { toast } from "sonner";

const inp = "w-full bg-[#101311] rounded-lg px-3 py-2.5 text-sm border border-emerald-800/50 outline-none focus:border-amber-400 text-[#FDFBF7]";
const FREE_SHIP_MIN = 75;
const SHIP_FLAT = 5.99;
const TAX_RATE = 0.055;

export default function MerchCartDrawer() {
  const { items, remove, setQty, subtotal, open, setOpen, clear } = useMerchCart();
  const { user } = useAuth();
  const [step, setStep] = useState("cart");
  const [busy, setBusy] = useState(false);
  const [f, setF] = useState({ customer_name: "", customer_email: "", customer_phone: "", address: "", city: "", state: "", zip_code: "", country: "US" });
  const set = (k, v) => setF((p) => ({ ...p, [k]: v }));

  useEffect(() => { if (!open) setStep("cart"); }, [open]);
  useEffect(() => {
    if (open && user) setF((p) => ({
      ...p,
      customer_name: p.customer_name || user.name || "",
      customer_email: p.customer_email || user.email || "",
      customer_phone: p.customer_phone || user.phone || "",
      address: p.address || user.address || "",
      zip_code: p.zip_code || user.zip_code || "",
    }));
  }, [open, user]);

  const shipping = subtotal >= FREE_SHIP_MIN || items.length === 0 ? 0 : SHIP_FLAT;
  const tax = useMemo(() => +(subtotal * TAX_RATE).toFixed(2), [subtotal]);
  const total = +(subtotal + shipping + tax).toFixed(2);
  const valid = f.customer_name && f.customer_email && f.address && f.city && f.state && f.zip_code && items.length;

  const pay = async () => {
    if (!valid) { toast.error("Please fill name, email, address, city, state and ZIP."); return; }
    setBusy(true);
    try {
      const { data: order } = await api.post("/merch/order", {
        ...f,
        items: items.map((i) => ({ product_id: i.product_id, size: i.size, color: i.color, quantity: i.quantity })),
      });
      localStorage.setItem("skk_last_order", order.order_number);
      const { data } = await api.post("/payments/checkout", { order_id: order.order_id, origin_url: window.location.origin });
      clear();
      window.location.href = data.checkout_url;
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Checkout is temporarily unavailable. Please try again in a moment.");
      setBusy(false);
    }
  };

  return (
    <Sheet open={open} onOpenChange={setOpen}>
      <SheetContent side="right" className="bg-[#101311] border-emerald-800/50 text-[#FDFBF7] w-full sm:max-w-md p-0 flex flex-col" data-testid="merch-cart-drawer">
        <div className="p-5 border-b border-emerald-800/50 flex items-center gap-2">
          {step === "checkout" && <button data-testid="merch-cart-back" onClick={() => setStep("cart")} className="text-amber-300 mr-1"><ChevronLeft className="w-5 h-5" /></button>}
          <ShoppingCart className="w-5 h-5 text-amber-400" />
          <span className="font-display font-black text-xl uppercase tracking-wide">{step === "cart" ? "Your Merch Cart" : "Checkout"}</span>
        </div>

        {items.length === 0 ? (
          <div className="flex-1 grid place-items-center text-center px-6">
            <div>
              <p className="text-[#FDFBF7]/60 mb-4" data-testid="merch-cart-empty">Your cart is empty.</p>
              <button onClick={() => setOpen(false)} className="px-6 py-3 rounded-full bg-amber-400 text-[#0A2818] font-display font-black uppercase tracking-wide">Shop Merch</button>
            </div>
          </div>
        ) : (
          <>
            <div className="flex-1 overflow-y-auto no-scrollbar p-5 space-y-4">
              {items.map((i) => (
                <div key={i.key} data-testid={`merch-cart-line-${i.product_id}`} className="flex gap-3">
                  <img src={i.image} alt={i.name} className="w-16 h-16 rounded-xl object-cover bg-[#181D1A]" />
                  <div className="flex-1 min-w-0">
                    <div className="flex justify-between gap-2">
                      <span className="font-display font-bold leading-tight">{i.name}</span>
                      <span className="text-amber-400 font-bold whitespace-nowrap">{money(i.price * i.quantity)}</span>
                    </div>
                    <div className="text-xs text-[#FDFBF7]/50">{[i.size, i.color].filter(Boolean).join(" · ")} · {money(i.price)} each</div>
                    <div className="flex items-center gap-2 mt-2">
                      <div className="flex items-center gap-2 bg-[#181D1A] rounded-full border border-emerald-800/50 px-1.5 py-1">
                        <button data-testid={`merch-cart-dec-${i.product_id}`} onClick={() => setQty(i.key, i.quantity - 1)} className="w-9 h-9 grid place-items-center active:scale-90 transition-transform"><Minus className="w-3.5 h-3.5" /></button>
                        <span className="text-sm w-5 text-center font-bold">{i.quantity}</span>
                        <button data-testid={`merch-cart-inc-${i.product_id}`} onClick={() => setQty(i.key, i.quantity + 1)} className="w-9 h-9 grid place-items-center active:scale-90 transition-transform"><Plus className="w-3.5 h-3.5" /></button>
                      </div>
                      <button data-testid={`merch-cart-remove-${i.product_id}`} onClick={() => remove(i.key)} className="text-[#FDFBF7]/40 hover:text-red-400"><Trash2 className="w-4 h-4" /></button>
                    </div>
                  </div>
                </div>
              ))}

              {step === "checkout" && (
                <div className="pt-2 space-y-2">
                  <div className="text-xs uppercase tracking-wider text-amber-400 font-bold flex items-center gap-2"><Truck className="w-4 h-4" /> Shipping details</div>
                  <div className="grid grid-cols-2 gap-2">
                    <input data-testid="merch-co-name" autoComplete="name" value={f.customer_name} onChange={(e) => set("customer_name", e.target.value)} placeholder="Full name*" className={inp} />
                    <input data-testid="merch-co-email" type="email" autoComplete="email" value={f.customer_email} onChange={(e) => set("customer_email", e.target.value)} placeholder="Email*" className={inp} />
                    <input data-testid="merch-co-phone" type="tel" autoComplete="tel" value={f.customer_phone} onChange={(e) => set("customer_phone", e.target.value)} placeholder="Phone" className={inp} />
                    <input data-testid="merch-co-city" autoComplete="address-level2" value={f.city} onChange={(e) => set("city", e.target.value)} placeholder="City*" className={inp} />
                    <input data-testid="merch-co-address" autoComplete="street-address" value={f.address} onChange={(e) => set("address", e.target.value)} placeholder="Shipping address*" className={`${inp} col-span-2`} />
                    <input data-testid="merch-co-state" autoComplete="address-level1" value={f.state} onChange={(e) => set("state", e.target.value)} placeholder="State* (e.g. ME)" className={inp} />
                    <input data-testid="merch-co-zip" inputMode="numeric" autoComplete="postal-code" value={f.zip_code} onChange={(e) => set("zip_code", e.target.value)} placeholder="ZIP*" className={inp} />
                  </div>
                </div>
              )}
            </div>

            <div className="border-t border-emerald-800/50 p-5 space-y-1.5">
              <Row label="Subtotal" value={money(subtotal)} />
              <Row label={`Shipping${shipping === 0 ? " (free over $75)" : ""}`} value={money(shipping)} />
              <Row label="Tax" value={money(tax)} />
              <div className="flex justify-between font-display font-black text-amber-400 text-lg pt-1">
                <span>Total</span><span data-testid="merch-cart-total">{money(total)}</span>
              </div>
              {step === "cart" ? (
                <button data-testid="merch-cart-checkout" onClick={() => setStep("checkout")} className="w-full mt-3 py-4 rounded-full bg-amber-400 hover:bg-amber-300 text-[#0A2818] font-display font-black uppercase tracking-wide active:scale-95 transition-transform">
                  Checkout · {money(total)}
                </button>
              ) : (
                <button data-testid="merch-pay-button" onClick={pay} disabled={busy || !valid} className="w-full mt-3 py-4 rounded-full bg-amber-400 hover:bg-amber-300 disabled:opacity-40 text-[#0A2818] font-display font-black uppercase tracking-wide flex items-center justify-center gap-2 active:scale-95 transition-transform">
                  {busy ? <><Loader2 className="w-5 h-5 animate-spin" /> Redirecting…</> : <><Lock className="w-4 h-4" /> Pay Securely · {money(total)}</>}
                </button>
              )}
              <p className="text-[10px] text-center text-[#FDFBF7]/40 pt-1">Secure payment via Stripe · Produced & shipped by our print partner · Test card 4242 4242 4242 4242</p>
            </div>
          </>
        )}
      </SheetContent>
    </Sheet>
  );
}

const Row = ({ label, value }) => (
  <div className="flex justify-between text-sm text-[#FDFBF7]/75"><span>{label}</span><span>{value}</span></div>
);
